import { Component, OnInit } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { TherapeuticClassProductAutoCompleteItem } from "src/app/ui/models/domains/auto-complete/therapeutic-class-product-auto-complete-item";
import { TherapeuticClassProductAutoCompleteSearchProvider } from "src/app/ui/models/domains/auto-complete/therapeutic-class-product-auto-complete-search-provider";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { ProductSubtype } from "src/app/ui/models/domains/pbm/Benefit/product-subtype";
import { ProductType } from "src/app/ui/models/domains/pbm/Benefit/product-type";
import { TherapeuticClass } from "src/app/ui/models/domains/pbm/Benefit/therapeutic-class";
import { PriceRegime } from "src/app/ui/models/domains/pbm/product-management/price-regime";
import { ProductUseValidity } from "src/app/ui/models/domains/pbm/product-management/product-use-validity";
import { TributaryCreditConcession } from "src/app/ui/models/domains/pbm/product-management/tributary-credit-concession";
import { UseType } from "src/app/ui/models/domains/pbm/product-management/use-type";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { ProductManagementService } from "src/app/ui/services/pbm/product-management.service";
import { TherapeuticClassService } from "src/app/ui/services/pbm/therapeutic-class.service";
import { Toast } from "../../../toast/toast";
import { ProductLabelInfoForm } from "../edit-product/product-label-info-form";
import { CreateProductValidator } from "./create-product-validator";
import { MatSelectChange } from "@angular/material/select";

@Component({
  selector: "insert-product",
  templateUrl: "./insert-product.component.html",
  styleUrls: ["./insert-product.component.scss"],
})
export class InsertProductComponent implements OnInit {
  public form: ProductLabelInfoForm;
  private productTypes: ProductType[];
  private productSubTypes: ProductSubtype[];
  private productUseValidities: ProductUseValidity[];
  private priceRegimes: PriceRegime[];
  private useTypes: UseType[];
  private tributaryCreditConcessions: TributaryCreditConcession[];
  public therapeuticClassProvider = new TherapeuticClassProductAutoCompleteSearchProvider(this.therapeuticClassService);

  constructor(
    private featureViewService: FeatureViewService,
    private productManagementService: ProductManagementService,
    private therapeuticClassService: TherapeuticClassService,
    private loadingService: LoadingService,
    private router: Router,
    private toast: Toast,
  ) {}

  async ngOnInit() {
    const loadingRef = this.loadingService.beginLoading();
    try {
      this.featureViewService.activeFeatureView(FeatureViews.EditProduct, PageViews.ProductManagementPageView);
      await this.loadLists();
      this.form = new ProductLabelInfoForm();
      this.form.createProductFormGroup();
    } catch {
      this.toast.showWarningToast("Erro ao buscar informações. Tente novamente.");
    } finally {
      loadingRef.closeLoading();
    }
  }

  private async loadLists(): Promise<void> {
    this.productTypes = await this.productManagementService.productTypes();
    this.productUseValidities = await this.productManagementService.productUseValidities();
    this.priceRegimes = await this.productManagementService.priceRegimes();
    this.useTypes = await this.productManagementService.useTypes();
    this.tributaryCreditConcessions = await this.productManagementService.tributaryCreditConcessions();
  }

  public getFormGroup(): UntypedFormGroup {
    return this.form && this.form.getFormGroup();
  }

  public getProductTypes(): ProductType[] {
    return this.productTypes;
  }

  public getProductSubTypes(): ProductSubtype[] {
    return this.productSubTypes;
  }

  public getProductUseValidities(): ProductUseValidity[] {
    return this.productUseValidities;
  }

  public getPriceRegimes(): PriceRegime[] {
    return this.priceRegimes;
  }

  public getUseTypes(): UseType[] {
    return this.useTypes;
  }

  public getTributaryCreditConcessions(): TributaryCreditConcession[] {
    return this.tributaryCreditConcessions;
  }

  public async onProductTypeChange(event: MatSelectChange): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    try {
      this.productSubTypes = await this.productManagementService.productSubTypesByType(event.value);
      this.form.clearSubTypeFormControl();
      this.form.enableSubTypeFormControl();
      if (this.productSubTypes.length === 0) {
        this.form.disableSubTypeFormControl();
        this.toast.showWarningToast(`Nenhum sutipo associado ao tipo de produto ${this.form.getFormGroup().value.productType.getValue()}`);
      }
    } catch (error) {
    } finally {
      loadingRef.closeLoading();
    }
  }

  public getAutoCompleteValue(value: TherapeuticClassProductAutoCompleteItem): void {
    const therapeuticClass = new TherapeuticClass();
    therapeuticClass.setTherapeuticClassKey(value.getTherapeuticClassKey());
    therapeuticClass.setDescription(value.getDescription());
    therapeuticClass.setCode(value.getCode());
    this.form.getFormGroup().patchValue({ therapeuticClass });
  }

  public goToSearchProduct(): void {
    this.router.navigate([RoutePath.SearchProduct]);
  }

  public async createProduct() {
    const loadingRef = this.loadingService.beginLoading();
    const validator = new CreateProductValidator(this.form);
    if (validator.isFormValid()) {
      try {
        const productLabelInfo = this.form.parseFormGroup();
        const productKey = await this.productManagementService.insertProduct(productLabelInfo);
        productLabelInfo.setProductKey(productKey);
        this.productManagementService.setProduct(productLabelInfo);
        this.toast.showSuccessToast("Produto criado com sucesso");
        this.router.navigate([RoutePath.EditProduct]);
      } catch (error) {
        this.toast.showWarningToast(error);
      } finally {
        loadingRef.closeLoading();
      }
    } else {
      this.toast.showWarningToast(validator.getErrorMessage());
      loadingRef.closeLoading();
    }
  }
}
